import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiWorker } from '../api/worker';
import { Issue, IssueCategory, IssueSeverity, IssueStatus } from '../types';
import toast from 'react-hot-toast';

/** Id the backend uses for the top platform level. */
export const PLATFORM_ROOT_ID = 'platform-root';

const unwrapPage = (r: any): Issue[] =>
  (r.data && Array.isArray(r.data.content)) ? r.data.content : (Array.isArray(r.data) ? r.data : []);

/**
 * Tickets addressed to a node — the node's actionable queue.
 *
 * `platform-root` is a valid target now, so no sentinel guard blocks it: the platform level is
 * exactly where a member's ticket lands when it is raised from outside any node.
 */
export const useSupportIssues = (nodeId: string, filters?: { category?: IssueCategory, severity?: IssueSeverity, status?: IssueStatus }) => {
  return useQuery<Issue[]>({
    queryKey: ['issues', nodeId, filters],
    queryFn: () => apiWorker.get(`/api/nodes/${nodeId}/issues`, {
      params: {
        category: filters?.category,
        severity: filters?.severity,
        status: filters?.status,
        page: 0,
        size: 50
      }
    }).then(unwrapPage),
    enabled: !!nodeId,
  });
};

/**
 * Tickets this node raised that have since been escalated above it. Read-only tracking: the
 * ticket has left this node's queue, so the node can follow it but not action it.
 */
export const useRaisedIssues = (nodeId: string, filters?: { status?: IssueStatus }) => {
  return useQuery<Issue[]>({
    queryKey: ['issues', 'raised', nodeId, filters],
    queryFn: () => apiWorker.get(`/api/nodes/${nodeId}/issues/raised`, {
      params: { status: filters?.status, page: 0, size: 50 }
    }).then(unwrapPage),
    enabled: !!nodeId && nodeId !== PLATFORM_ROOT_ID,
  });
};

/** A single ticket, including its comment thread and escalation trail. */
export const useIssue = (id: string) => {
  return useQuery<Issue>({
    queryKey: ['issue', id],
    queryFn: () => apiWorker.get(`/api/issues/${id}`).then(r => r.data),
    enabled: !!id,
  });
};

const invalidateTicketViews = (queryClient: ReturnType<typeof useQueryClient>, nodeId?: string) => {
  queryClient.invalidateQueries({ queryKey: ['issues'] });
  queryClient.invalidateQueries({ queryKey: ['issue'] });
  if (nodeId) {
    queryClient.invalidateQueries({ queryKey: ['issues', nodeId] });
  }
};

export const useCreateIssue = (nodeId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Issue>) =>
      apiWorker.post(`/api/nodes/${nodeId}/issues`, data).then(r => r.data),
    onSuccess: (created: Issue) => {
      invalidateTicketViews(queryClient, nodeId);
      // The addressed-to node is where the ticket now lives, which may not be this node.
      if (created?.nodeId && created.nodeId !== nodeId) {
        queryClient.invalidateQueries({ queryKey: ['issues', created.nodeId] });
        toast.success(`Ticket sent to ${created.addressedToName || created.nodeId}`);
      } else {
        toast.success('Ticket submitted successfully');
      }
    }
  });
};

export const useUpdateIssueStatus = (nodeId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string, data: Partial<Issue> }) =>
      apiWorker.patch(`/api/issues/${id}/status`, data).then(r => r.data),
    onSuccess: () => {
      invalidateTicketViews(queryClient, nodeId);
      toast.success('Ticket updated');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Could not update the ticket');
    }
  });
};

/**
 * Escalates a ticket one level up its hierarchy. The backend picks the target from the ticket's
 * current addressee, so there is nothing to choose here.
 */
export const useEscalateIssue = (nodeId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      apiWorker.post(`/api/issues/${id}/escalate`).then(r => r.data),
    onSuccess: (updated: Issue) => {
      invalidateTicketViews(queryClient, nodeId);
      toast.success(
        updated?.escalatedToSystem
          ? 'Escalated to the platform'
          : `Escalated to ${updated?.addressedToName || 'the parent node'}`
      );
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Could not escalate the ticket');
    }
  });
};

export const useAddIssueComment = (nodeId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, message }: { id: string, message: string }) =>
      apiWorker.post(`/api/issues/${id}/comments`, { message }).then(r => r.data),
    onSuccess: () => {
      invalidateTicketViews(queryClient, nodeId);
      toast.success('Reply added');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Could not add your reply');
    }
  });
};